import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/common/Sidebar';
import ValidationSummary from '../../components/chef/ValidationSummary';
import PlanQueue from '../../components/chef/PlanQueue';
import ValidationInterface from '../../components/chef/ValidationInterface';
import PlanSubmissionForm from '../../components/teacher/PlanSubmissionForm';
import PaginationControls from '../../components/common/PaginationControls';
import NoticeToast from '../../components/common/NoticeToast';
import { getPlansToValidate, getSummary, updatePlanStatus, saveGrid, getPlanGrilleData } from '../../api/chef';
import { submitPlan, getPlans } from '../../api/teacher';
import { useAuth } from '../../auth/AuthContext';
import { apiUrl } from '../../config/api';
import { useNotice } from '../../hooks/useNotice';
import { getFileExtension, openOrDownloadDocument, safeFilename } from '../../utils/documentHandling';
import './ChefDashboard.css';

const FILE_BASE = apiUrl();
const ITEMS_PER_PAGE = 10;

export default function ChefDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { notice, showNotice, clearNotice } = useNotice();
  const [activeTab, setActiveTab] = useState('validation'); // 'validation' or 'submit'
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, completed: 0, revise: 0 });
  const [filters, setFilters] = useState({ 
    status: 'Tous', 
    teacher: '', 
    course: '',
    session: 'Toutes',
    programme: 'Tous',
    mode: 'Tous',
    campus: 'Tous',
    sortBy: 'submittedAt',
    sortDirection: 'desc'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [planPayload, setPlanPayload] = useState(null);
  const [loadingPayload, setLoadingPayload] = useState(false);
  const [chefPlans, setChefPlans] = useState([]);
  const [queuePage, setQueuePage] = useState(1);
  const [chefPlansPage, setChefPlansPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const validationSectionRef = useRef(null);
  const submitSectionRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      if (!user?.id) return;
      setLoading(true);
      setError('');
      try {
        const plans = await getPlansToValidate(user.id);
        const summaryData = await getSummary(user.id);
        setQueue(plans);
        setSummary(summaryData);
      } catch (err) {
        setError(err.message || 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user?.id]);

  // Charger les plans soumis par le chef
  useEffect(() => {
    const loadChefPlans = async () => {
      if (!user?.id) return;
      try {
        const plans = await getPlans(user.id);
        setChefPlans(plans);
      } catch (err) {
        console.error('Erreur chargement plans du chef:', err);
      }
    };
    loadChefPlans();
  }, [user?.id]);

  const refreshQueue = async () => {
    if (!user?.id) return;
    try {
      const plans = await getPlansToValidate(user.id);
      const summaryData = await getSummary(user.id);
      setQueue(plans);
      setSummary(summaryData);
    } catch (err) {
      console.error('Erreur rafraîchissement:', err);
    }
  };

  const refreshChefPlans = async () => {
    if (!user?.id) return;
    try {
      const plans = await getPlans(user.id);
      setChefPlans(plans);
    } catch (err) {
      console.error('Erreur rafraîchissement plans chef:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      await Promise.all([refreshQueue(), refreshChefPlans()]);
    } catch (err) {
      setError('Erreur lors du rafraîchissement');
    } finally {
      setRefreshing(false);
    }
  };

  const normalizedQueue = useMemo(() => {
    const statusLabelMap = {
      'non_analyse': 'en analyse',
      'analyse': 'en analyse',
      'validé': 'validé',
      'approuvé': 'approuvé',
      'non validé': 'non validé',
    };
    const statusClassMap = {
      'non_analyse': 'analyse',
      'analyse': 'analyse',
      'validé': 'valide',
      'approuvé': 'approuve',
      'non validé': 'non-valide',
    };
    return queue.map((plan) => {
      const rawStatus = (plan.statut || '').toLowerCase();
      const mappedStatus = statusLabelMap[rawStatus] || rawStatus || 'en analyse';
      const statusClass = statusClassMap[rawStatus] || 'analyse';
      const safeName = safeFilename(plan.nom_fichier);
      const fileExtension = getFileExtension(plan.nom_fichier);
      return {
        id: plan.id,
        title: plan.nom_fichier ? plan.nom_fichier.replace(/\.(pdf|doc|docx)$/i, '') : plan.cours,
        rawFilename: plan.nom_fichier || '',
        fileExtension,
        course: plan.cours || 'N/A',
        teacher: plan.enseignant || '—',
        statusLabel: mappedStatus,
        statusClass,
        statusKey: rawStatus === 'non_analyse' ? 'analyse' : (rawStatus || 'analyse'),
        assignedTo: plan.chef_nom || '—',
        submittedAt: (plan.date_upload || '').slice(0, 10),
        session: plan.session || 'N/A',
        departement: plan.departement || 'N/A',
        programme: plan.programme || 'N/A',
        mode_enseignement: plan.mode_enseignement || 'N/A',
        campus: plan.campus || 'N/A',
        pdfUrl: plan.nom_fichier ? `${FILE_BASE}/static/plans/${plan.id}_${safeName}` : '#',
      };
    });
  }, [queue]);

  const filteredQueue = useMemo(() => {
    return normalizedQueue.filter((plan) => {
      const statusOk = filters.status === 'Tous' || plan.statusKey === filters.status;
      const teacherOk = !filters.teacher || plan.teacher.toLowerCase().includes(filters.teacher.toLowerCase());
      const courseOk = !filters.course || plan.course.toLowerCase().includes(filters.course.toLowerCase());
      const sessionOk = filters.session === 'Toutes' || plan.session === filters.session;
      const programmeOk = filters.programme === 'Tous' || plan.programme === filters.programme;
      const modeOk = filters.mode === 'Tous' || plan.mode_enseignement === filters.mode;
      const campusOk = filters.campus === 'Tous' || plan.campus === filters.campus;
      return statusOk && teacherOk && courseOk && sessionOk && programmeOk && modeOk && campusOk;
    });
  }, [normalizedQueue, filters]);

  const sortedFilteredQueue = useMemo(() => {
    const statusRank = {
      analyse: 0,
      'non validé': 1,
      validé: 2,
      approuvé: 3,
      'non approuvé': 4,
    };

    const compareText = (left, right) => String(left || '').localeCompare(String(right || ''), 'fr', { sensitivity: 'base' });

    return [...filteredQueue].sort((a, b) => {
      const directionFactor = filters.sortDirection === 'asc' ? 1 : -1;

      if (filters.sortBy === 'submittedAt') {
        const dateA = new Date(a.submittedAt || 0).getTime();
        const dateB = new Date(b.submittedAt || 0).getTime();
        if (dateA !== dateB) {
          return (dateA - dateB) * directionFactor;
        }
      } else if (filters.sortBy === 'statusKey') {
        const rankA = statusRank[a.statusKey] ?? 999;
        const rankB = statusRank[b.statusKey] ?? 999;
        if (rankA !== rankB) {
          return (rankA - rankB) * directionFactor;
        }
      } else {
        const valueA = a[filters.sortBy];
        const valueB = b[filters.sortBy];
        const textCompare = compareText(valueA, valueB);
        if (textCompare !== 0) {
          return textCompare * directionFactor;
        }
      }

      return b.id - a.id;
    });
  }, [filteredQueue, filters.sortBy, filters.sortDirection]);

  const handleSortChange = (sortBy, sortDirection) => {
    if (!sortDirection) {
      setFilters((prev) => ({ ...prev, sortBy: 'submittedAt', sortDirection: 'desc' }));
      return;
    }
    setFilters((prev) => ({ ...prev, sortBy, sortDirection }));
  };

  const totalQueuePages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedFilteredQueue.length / ITEMS_PER_PAGE));
  }, [sortedFilteredQueue.length]);

  const paginatedQueue = useMemo(() => {
    const start = (queuePage - 1) * ITEMS_PER_PAGE;
    return sortedFilteredQueue.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedFilteredQueue, queuePage]);

  const sortedChefPlans = useMemo(() => {
    return [...chefPlans].sort((a, b) => {
      const dateA = new Date(a.updated_at || a.date_upload || 0).getTime();
      const dateB = new Date(b.updated_at || b.date_upload || 0).getTime();
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      return b.id - a.id;
    });
  }, [chefPlans]);

  const totalChefPlansPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedChefPlans.length / ITEMS_PER_PAGE));
  }, [sortedChefPlans.length]);

  const paginatedChefPlans = useMemo(() => {
    const start = (chefPlansPage - 1) * ITEMS_PER_PAGE;
    return sortedChefPlans.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedChefPlans, chefPlansPage]);

  const sidebarTeamAssignments = useMemo(() => {
    const counts = normalizedQueue.reduce((acc, plan) => {
      const teacherName = (plan.teacher || 'Non assigné').trim() || 'Non assigné';
      acc[teacherName] = (acc[teacherName] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([name, plans]) => ({ id: name, name, plans }))
      .sort((a, b) => b.plans - a.plans)
      .slice(0, 5);
  }, [normalizedQueue]);

  const monthlyPerformance = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const doneStatuses = new Set(['validé', 'approuvé', 'non validé']);

    const monthlyPlans = normalizedQueue.filter((plan) => {
      if (!plan.submittedAt) return false;
      const submittedDate = new Date(plan.submittedAt);
      return submittedDate.getMonth() === currentMonth && submittedDate.getFullYear() === currentYear;
    });

    const completedMonthlyPlans = monthlyPlans.filter((plan) => doneStatuses.has((plan.statusLabel || '').toLowerCase()));
    const rate = monthlyPlans.length > 0
      ? Math.round((completedMonthlyPlans.length / monthlyPlans.length) * 100)
      : 0;

    const delays = completedMonthlyPlans
      .map((plan) => {
        if (!plan.submittedAt) return null;
        const submittedDate = new Date(plan.submittedAt);
        const diffMs = now.getTime() - submittedDate.getTime();
        return Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
      })
      .filter((value) => value !== null);

    const delay = delays.length > 0
      ? Math.round(delays.reduce((sum, value) => sum + value, 0) / delays.length)
      : 0;

    return { rate, delay };
  }, [normalizedQueue]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    setQueuePage(1);
  }, [filters]);

  useEffect(() => {
    if (queuePage > totalQueuePages) {
      setQueuePage(totalQueuePages);
    }
  }, [queuePage, totalQueuePages]);

  useEffect(() => {
    if (chefPlansPage > totalChefPlansPages) {
      setChefPlansPage(totalChefPlansPages);
    }
  }, [chefPlansPage, totalChefPlansPages]);

  useEffect(() => {
    if (!sortedFilteredQueue.length) {
      setSelectedPlanId(null);
      return;
    }
    setSelectedPlanId((prev) => (prev && sortedFilteredQueue.some((plan) => plan.id === prev)
      ? prev
      : sortedFilteredQueue[0].id));
  }, [sortedFilteredQueue]);

  const selectedPlan = useMemo(() => (
    sortedFilteredQueue.find((plan) => plan.id === selectedPlanId) || null
  ), [sortedFilteredQueue, selectedPlanId]);

  useEffect(() => {
    const loadPayload = async () => {
      if (!selectedPlanId) {
        setPlanPayload(null);
        return;
      }
      setLoadingPayload(true);
      try {
        const data = await getPlanGrilleData(selectedPlanId);
        setPlanPayload(data.payload || {});
      } catch (err) {
        console.error('Erreur chargement payload:', err);
        setPlanPayload({});
      } finally {
        setLoadingPayload(false);
      }
    };
    loadPayload();
  }, [selectedPlanId]);

  const handleSelectPlan = (plan) => {
    if (!plan) return;
    setSelectedPlanId(plan.id);
  };

  const handlePreviewPlan = (plan) => {
    openOrDownloadDocument({
      url: plan?.pdfUrl,
      fileName: plan?.rawFilename,
      onOpenPdf: () => window.open(plan.pdfUrl, '_blank', 'noopener,noreferrer'),
      onNotify: showNotice,
    });
  };

  const handleStatusChange = async (statut) => {
    if (!selectedPlan) return;
    setStatusUpdating(true);
    setError('');
    try {
      await updatePlanStatus(selectedPlan.id, statut);
      await refreshQueue();
      setError(''); // Clear error on success
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour du statut');
      // Ne pas rafraîchir la liste en cas d'erreur pour éviter qu'elle disparaisse
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleSaveGrid = async (payload) => {
    if (!selectedPlan || !user?.id) return;
    setStatusUpdating(true);
    setError('');
    try {
      await saveGrid(selectedPlan.id, user.id, payload);
      await refreshQueue();
    } catch (err) {
      setError(err.message || 'Erreur lors de l’enregistrement');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleOpenResult = async (plan) => {
    if (!plan) return;
    try {
      const response = await fetch(apiUrl(`/api/plans/${plan.id}/grille`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('Grille indisponible');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      setError(err.message || 'Erreur lors de l’ouverture de la grille');
    }
  };

  const handleSubmitPlan = async (payload) => {
    if (!user?.id) return;
    payload.append('teacher_id', user.id);
    payload.append('cours', payload.get('classe_nom') || '');
    await submitPlan(payload);
    // Switch back to validation tab after successful submission
    setActiveTab('validation');
    showNotice('Plan soumis avec succes. Il sera valide par le conseiller pedagogique.', 'success');
  };

  const handleSidebarNavigate = (sectionKey) => {
    if (sectionKey === 'submit') {
      setActiveTab('submit');
      setTimeout(() => {
        submitSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return;
    }

    setActiveTab('validation');
    setTimeout(() => {
      validationSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  return (
    <div className="chef-dashboard">
      {/* Barre de navigation supérieure */}
      <header className="chef-topbar">
        <div className="topbar-left">
          <img className="logo-small" src={`${process.env.PUBLIC_URL}/veriplan-wordmark.svg`} alt="VeriPlan" />
          <h1>Chef d'équipe</h1>
        </div>
        <div className="topbar-right">
          <span className="user-name">👤 {user?.nom || 'Chef d\'équipe'}</span>
          <button className="btn-logout" onClick={logout} type="button">
            🚪 Déconnexion
          </button>
        </div>
      </header>

      <Sidebar 
        active={activeTab === 'submit' ? 'submit' : 'validation'}
        onNavigate={handleSidebarNavigate}
        team={sidebarTeamAssignments}
        performanceRate={monthlyPerformance.rate}
        performanceDelay={monthlyPerformance.delay}
        showHistory={false}
      />

      <section className="chef-main">
        <header className="chef-header">
          <div>
            <h2>Tableau de bord</h2>
            <p className="muted">Supervisez les validations et gérez vos plans.</p>
          </div>
          <div className="chef-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              type="button"
              disabled={refreshing}
            >
              {refreshing ? 'Rafraîchissement...' : 'Actualiser le tableau'}
            </button>
            <button 
              className={`btn-tab ${activeTab === 'validation' ? 'active' : ''}`}
              onClick={() => setActiveTab('validation')}
              type="button"
              style={{
                padding: '8px 20px',
                borderRadius: '6px',
                border: activeTab === 'validation' ? '2px solid #6366f1' : '2px solid #e5e7eb',
                backgroundColor: activeTab === 'validation' ? '#eef2ff' : 'white',
                color: activeTab === 'validation' ? '#4f46e5' : '#6b7280',
                fontWeight: activeTab === 'validation' ? '600' : '400',
                cursor: 'pointer',
                marginRight: '8px'
              }}
            >
              📋 Valider les plans
            </button>
            <button 
              className={`btn-tab ${activeTab === 'submit' ? 'active' : ''}`}
              onClick={() => setActiveTab('submit')}
              type="button"
              style={{
                padding: '8px 20px',
                borderRadius: '6px',
                border: activeTab === 'submit' ? '2px solid #6366f1' : '2px solid #e5e7eb',
                backgroundColor: activeTab === 'submit' ? '#eef2ff' : 'white',
                color: activeTab === 'submit' ? '#4f46e5' : '#6b7280',
                fontWeight: activeTab === 'submit' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              📤 Soumettre mon plan
            </button>
          </div>
        </header>

        {activeTab === 'validation' ? (
          <div ref={validationSectionRef}>
            <ValidationSummary summary={summary} />

            {loading ? (
              <p className="muted">Chargement des plans...</p>
            ) : error ? (
              <p className="error">{error}</p>
            ) : (
              <PlanQueue
                plans={paginatedQueue}
                allPlans={sortedFilteredQueue}
                filters={filters}
                onFilterChange={updateFilter}
                onSortChange={handleSortChange}
                selectedPlanId={selectedPlanId}
                onSelectPlan={handleSelectPlan}
                onOpenPreview={handlePreviewPlan}
                currentPage={queuePage}
                totalPages={totalQueuePages}
                onPageChange={setQueuePage}
              />
            )}

            <ValidationInterface
              plan={selectedPlan}
              onStatusChange={handleStatusChange}
              statusUpdating={statusUpdating}
              onSaveGrid={handleSaveGrid}
              onOpenResult={handleOpenResult}
              initialPayload={planPayload}
              loadingPayload={loadingPayload}
              verifierName={user?.nom || ''}
            />
          </div>
        ) : (
          <div ref={submitSectionRef} style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            padding: '24px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            {/* Plans soumis par le chef */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>📋 Mes plans soumis</h3>
              {chefPlans.length === 0 ? (
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '8px',
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  Aucun plan soumis pour le moment
                </div>
              ) : (
                <div style={{ overflow: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Cours</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Statut</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Soumis</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedChefPlans.map((plan) => (
                        <tr key={plan.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px' }}>{plan.cours}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: plan.statut === 'approuvé' ? '#dcfce7' : plan.statut === 'validé' ? '#dbeafe' : '#fef3c7',
                              color: plan.statut === 'approuvé' ? '#166534' : plan.statut === 'validé' ? '#1e40af' : '#b45309'
                            }}>
                              {plan.statut}
                            </span>
                          </td>
                          <td style={{ padding: '12px', color: '#6b7280', fontSize: '13px' }}>
                            {(plan.date_upload || '').slice(0, 10)}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div className="chef-submitted-actions">
                              <button
                                type="button"
                                className="chef-submitted-btn"
                                onClick={() => handlePreviewPlan({
                                  ...plan,
                                  rawFilename: plan.nom_fichier || '',
                                  pdfUrl: plan.nom_fichier
                                    ? `${FILE_BASE}/static/plans/${plan.id}_${safeFilename(plan.nom_fichier || '')}`
                                    : '#',
                                })}
                              >
                                Visualiser
                              </button>
                              <button
                                type="button"
                                className="chef-submitted-btn chef-submitted-grid-btn"
                                onClick={() => navigate(`/grid/${plan.id}`)}
                              >
                                📋 Voir grille
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <PaginationControls
                    currentPage={chefPlansPage}
                    totalPages={totalChefPlansPages}
                    onPageChange={setChefPlansPage}
                  />
                </div>
              )}
            </div>

            {/* Formulaire de soumission */}
            <div style={{
              paddingTop: '24px',
              borderTop: '2px solid #e5e7eb'
            }}>
              <h3 style={{ marginBottom: '16px', color: '#1f2937' }}>📤 Soumettre un nouveau plan</h3>
              <div style={{
                padding: '16px',
                marginBottom: '24px',
                backgroundColor: '#e3f2fd',
                border: '1px solid #2196f3',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#1565c0'
              }}>
                ℹ️ <strong>Chef d'équipe:</strong> Vos plans seront validés directement par un conseiller pédagogique (pas de validation par un autre chef d'équipe).
              </div>
              <PlanSubmissionForm onSubmit={handleSubmitPlan} hideChefSelection={true} />
            </div>
          </div>
        )}
      </section>

      <NoticeToast notice={notice} onClose={clearNotice} />

    </div>
  );
}
